import React, { useState } from "react";
import {
  BsLock,
  BsCurrencyDollar,
  BsCheckCircle,
  BsCalendarEvent,
  BsGift,
  BsBook,
  BsAward,
  BsShop,
  BsPercent,
  BsPlusCircle,
  BsTrash,
  BsStars,
} from "react-icons/bs";
import { FiPlus } from "react-icons/fi";

// Predefined perks checklist
const perkChecklistItems = [
  {
    key: "privateChannels",
    label: "Private Discord/Telegram Channels",
    icon: BsLock,
    description: "Exclusive access to token-gated channels",
  },
  {
    key: "priorityInvestment",
    label: "Priority Investment Access",
    icon: BsCurrencyDollar,
    description: "First access to invest in new projects",
  },
  {
    key: "votingRights",
    label: "Governance & Voting Rights",
    icon: BsCheckCircle,
    description: "Vote on community decisions (DAO)",
  },
  {
    key: "exclusiveEvents",
    label: "Exclusive Events & Webinars",
    icon: BsCalendarEvent,
    description: "Invitations to private AMAs and meetups",
  },
  {
    key: "futureAirdrops",
    label: "Future Airdrops",
    icon: BsGift,
    description: "Receive tokens and rewards",
  },
  {
    key: "earlyResearchAccess",
    label: "Early Research Access",
    icon: BsBook,
    description: "Preview research findings before public release",
  },
  {
    key: "specialRole",
    label: "Special Discord Role",
    icon: BsAward,
    description: "Verified member badge and recognition",
  },
  {
    key: "merchandise",
    label: "Branded Merchandise",
    icon: BsShop,
    description: "Exclusive community merch and swag",
  },
  {
    key: "partnerDiscounts",
    label: "Partner Discounts",
    icon: BsPercent,
    description: "Special deals from community partners",
  },
];

const perkCategories = [
  { value: "exclusive_access", label: "Exclusive Access" },
  { value: "governance", label: "Governance" },
  { value: "rewards", label: "Rewards" },
  { value: "real_world", label: "Real World" },
  { value: "gamification", label: "Gamification" },
  { value: "desci_specific", label: "DeSci Specific" },
  { value: "other", label: "Other" },
];

const Step4PerksAccess = ({ formik }) => {
  const [newPerk, setNewPerk] = useState({ title: "", description: "", category: "other" });

  const handleChecklistChange = (key, checked) => {
    formik.setFieldValue(`perkChecklist.${key}`, checked);
  };

  const handleAddCustomPerk = () => {
    if (!newPerk.title.trim()) return;

    const currentPerks = formik.values.perks || [];
    formik.setFieldValue("perks", [
      ...currentPerks,
      { ...newPerk, active: true },
    ]);
    setNewPerk({ title: "", description: "", category: "other" });
  };

  const handleRemovePerk = (index) => {
    const currentPerks = [...(formik.values.perks || [])];
    currentPerks.splice(index, 1);
    formik.setFieldValue("perks", currentPerks);
  };

  const selectedPerksCount =
    Object.values(formik.values.perkChecklist || {}).filter(Boolean).length +
    (formik.values.perks?.length || 0);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-yellow-100 to-amber-100 mb-3">
          <BsStars className="text-2xl md:text-3xl text-yellow-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          Perks & Access
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Define what benefits your community members will receive
        </p>
        <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
          <BsAward />
          <span>{selectedPerksCount} perks selected</span>
        </div>
      </div>

      {/* Quick Perk Checklist */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BsCheckCircle className="text-primary" />
          Quick Select Perks
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {perkChecklistItems.map((item) => {
            const Icon = item.icon;
            const isChecked = formik.values.perkChecklist?.[item.key] || false;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleChecklistChange(item.key, !isChecked)}
                className={`p-4 rounded-xl border-2 text-left transition-all group ${
                  isChecked
                    ? "border-primary bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isChecked
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="text-lg" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800 text-sm">
                      {item.label}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {item.description}
                    </p>
                  </div>
                  {isChecked && (
                    <BsCheckCircle className="text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Perks */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BsPlusCircle className="text-primary" />
          Custom Perks
        </h3>

        {/* Existing Custom Perks */}
        {formik.values.perks?.length > 0 && (
          <div className="space-y-3 mb-4">
            {formik.values.perks.map((perk, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{perk.title}</h4>
                  {perk.description && (
                    <p className="text-sm text-gray-500">{perk.description}</p>
                  )}
                  <span className="inline-block mt-1 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                    {perkCategories.find((c) => c.value === perk.category)?.label || perk.category}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemovePerk(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <BsTrash />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add New Perk Form */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Perk Title
              </label>
              <input
                type="text"
                value={newPerk.title}
                onChange={(e) =>
                  setNewPerk({ ...newPerk, title: e.target.value })
                }
                placeholder="e.g., Monthly Research Reports"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={newPerk.category}
                onChange={(e) =>
                  setNewPerk({ ...newPerk, category: e.target.value })
                }
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
              >
                {perkCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={newPerk.description}
              onChange={(e) =>
                setNewPerk({ ...newPerk, description: e.target.value })
              }
              placeholder="Describe what this perk includes..."
              rows={2}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              maxLength={500}
            />
          </div>
          <button
            type="button"
            onClick={handleAddCustomPerk}
            disabled={!newPerk.title.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiPlus />
            Add Perk
          </button>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
        <h4 className="font-semibold text-yellow-800 mb-2">💡 Perk Ideas for DeSci Communities</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Early access to dataset releases and research papers</li>
          <li>• Co-authorship opportunities on community publications</li>
          <li>• Verified researcher/supporter badge on FunDeSci</li>
          <li>• Revenue share from project commercialization</li>
          <li>• Dynamic NFT that evolves based on contribution level</li>
        </ul>
      </div>
    </div>
  );
};

export default Step4PerksAccess;
